import { useState, useEffect, useMemo, useCallback } from 'react';
import { OnboardingReturnBanner } from '@/components/onboarding/OnboardingReturnBanner';
import { Search, Grid3x3, List, Mail, MapPin, Briefcase, Building2, X, Eye, Loader2, Users, Filter, Check, ChevronsUpDown, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, SlidersHorizontal, Settings2, RotateCcw, GripVertical } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { BulkSelectionBar } from '@/components/resources/BulkSelectionBar';
import { BulkUpdateCompanyAccessDialog } from '@/components/resources/BulkUpdateCompanyAccessDialog';
// Resources page with card and list views
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multiselect';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useScheduler, mapApiResourceToResource } from '@/contexts/SchedulerContext';
import { useScreenData } from '@/hooks/useScreenData';
import { useDataMode } from '@/contexts/DataModeContext';
import { resourcesApi } from '@/services/api';
import { ResourceStatus, WorkingHoursProfile } from '@/types';
import { calculateResourceUtilization } from '@/lib/utils/calendar';
import { DataModeToggle } from '@/components/DataModeToggle';
import { HelpButton } from '@/components/help/HelpButton';
import { ResourceDetailsModal } from '@/components/resources/ResourceDetailsModal';
import { usePermissions } from '@/contexts/PermissionsContext';
import { useUser } from '@/contexts/UserContext';
import { toast } from 'sonner';
import { 
  startOfDay, 
  endOfDay, 
  startOfWeek, 
  endOfWeek, 
  startOfMonth, 
  endOfMonth, 
  startOfQuarter, 
  endOfQuarter, 
  startOfYear, 
  endOfYear,
  addDays
} from 'date-fns';

type UtilizationPeriod = 'day' | 'week' | 'month' | 'quarter' | 'year';
type ViewMode = 'grid' | 'card';

export function Resources() {
  const { canViewResourceDetails, getResourceDirectoryViewAccessLevel } = usePermissions();
  const { user } = useUser();
  const { updateResource, isLoading: isContextLoading, mockData } = useScheduler();
  const { resources: contextResources, bookings, settings, currentMode } = useScreenData('resources');
  const { getScreenMode } = useDataMode();
  const isDemo = getScreenMode('resources') === 'demo';

  // Resource Directory shows ALL resources across every company (not filtered
  // by the currently selected company).  In demo mode we use allResources from
  // mockData which includes every mock resource regardless of company selection.
  const [allResources, setAllResources] = useState<typeof contextResources>([]);
  const [allResourcesLoading, setAllResourcesLoading] = useState(false);

  useEffect(() => {
    if (isDemo) {
      setAllResources(mockData.allResources);
      return;
    }
    let cancelled = false;
    setAllResourcesLoading(true);
    resourcesApi.getAllForOrganisation()
      .then(data => {
        if (!cancelled) setAllResources(data.map(mapApiResourceToResource));
      })
      .catch(() => {
        if (!cancelled) setAllResources(contextResources);
      })
      .finally(() => {
        if (!cancelled) setAllResourcesLoading(false);
      });
    return () => { cancelled = true; };
  }, [isDemo, contextResources, mockData.allResources]);

  const allVisibleResources = isDemo ? mockData.allResources : allResources;

  // Scope the visible resources by the role's "View Resources" access level
  // before any search/filter/sort logic runs. Matches the logged-in user to
  // their resource record by email (same pattern as SchedulerContext):
  //  - 'own'  → only the user's own resource record
  //  - 'team' → resources sharing the user's department (falls back to the own
  //             record when the department is unknown)
  //  - 'all'  → every resource card
  const viewAccessLevel = getResourceDirectoryViewAccessLevel();
  const resources = useMemo(() => {
    if (viewAccessLevel === 'all') return allVisibleResources;

    const userEmail = user.email?.toLowerCase();
    const ownResource = userEmail
      ? allVisibleResources.find(r => r.email?.toLowerCase() === userEmail)
      : undefined;

    if (!ownResource) return [];

    if (viewAccessLevel === 'own') {
      return [ownResource];
    }

    // 'team' — resources in the same department as the logged-in user
    const ownDepartment = ownResource.department?.toLowerCase();
    if (!ownDepartment) return [ownResource];
    return allVisibleResources.filter(
      r => r.department?.toLowerCase() === ownDepartment
    );
  }, [allVisibleResources, viewAccessLevel, user.email]);

  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState<string[]>([]);
  const [roleFilter, setRoleFilter] = useState<string[]>([]);
  const [locationFilter, setLocationFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [utilizationPeriod, setUtilizationPeriod] = useState<UtilizationPeriod>('month');
  const [viewMode, setViewMode] = useState<ViewMode>('card');

  // Table sort state
  type SortField = 'name' | 'role' | 'department' | 'email' | 'phone' | 'location' | 'type';
  type SortDir = 'asc' | 'desc';
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Column visibility
  type ResourceCol = 'name' | 'role' | 'department' | 'email' | 'phone' | 'location' | 'type';
  const ALL_RESOURCE_COLS: { key: ResourceCol; label: string; description: string; locked?: boolean }[] = [
    { key: 'name',       label: 'Name',       description: 'Resource full name',                    locked: true  },
    { key: 'role',       label: 'Role',       description: 'Job title or functional role'                         },
    { key: 'department', label: 'Department', description: 'Team or department the resource belongs to'           },
    { key: 'email',      label: 'Email',      description: 'Primary contact email address'                        },
    { key: 'phone',      label: 'Phone',      description: 'Contact phone number'                                 },
    { key: 'location',   label: 'Location',   description: 'Office or remote working location'                    },
    { key: 'type',       label: 'Type',       description: 'Employment type — person, contractor, or agency'      },
  ];
  const [visibleResCols, setVisibleResCols] = useState<Set<ResourceCol>>(
    new Set(ALL_RESOURCE_COLS.map((c) => c.key))
  );
  const [isResColConfigOpen, setIsResColConfigOpen] = useState(false);
  const toggleResCol = (key: ResourceCol) =>
    setVisibleResCols((prev) => {
      const col = ALL_RESOURCE_COLS.find((c) => c.key === key);
      if (col?.locked) return prev;
      const next = new Set(prev);
      if (next.has(key)) { if (next.size > 1) next.delete(key); }
      else next.add(key);
      return next;
    });
  const resetResCols = () => setVisibleResCols(new Set(ALL_RESOURCE_COLS.map((c) => c.key)));

  // Pagination
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedResource, setSelectedResource] = useState<typeof resources[number] | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [skillFilter, setSkillFilter] = useState<string[]>([]);

  // Pending (draft) filter state — only applied when user clicks "Apply Filters"
  const [pendingDepartmentFilter, setPendingDepartmentFilter] = useState<string[]>([]);
  const [pendingRoleFilter, setPendingRoleFilter] = useState<string[]>([]);
  const [pendingLocationFilter, setPendingLocationFilter] = useState<string[]>([]);
  const [pendingStatusFilter, setPendingStatusFilter] = useState<string[]>([]);
  const [pendingSkillFilter, setPendingSkillFilter] = useState<string[]>([]);

  // --- Bulk selection ---
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkUpdateDialog, setShowBulkUpdateDialog] = useState(false);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  // Check if a photoUrl is a valid, loadable image URL
  const isValidPhotoUrl = (url: string | undefined | null): boolean => {
    if (!url || url.trim() === '') return false;
    // Filter out common placeholder/invalid patterns
    if (url === 'null' || url === 'undefined') return false;
    return true;
  };

  const getStatusColor = (status: ResourceStatus) => {
    switch (status) {
      case 'available':
      case 'active':
        return 'bg-green-500';
      case 'busy':
        return 'bg-red-500';
      case 'off':
      case 'inactive':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusLabel = (status: ResourceStatus) => {
    switch (status) {
      case 'available':
      case 'active':
        return 'active';
      case 'busy':
        return 'active';
      case 'off':
      case 'inactive':
        return 'inactive';
      default:
        return 'active';
    }
  };

  const DAYS_ORDER: { key: keyof WorkingHoursProfile; label: string }[] = [
    { key: 'monday', label: 'Mon' },
    { key: 'tuesday', label: 'Tue' },
    { key: 'wednesday', label: 'Wed' },
    { key: 'thursday', label: 'Thu' },
    { key: 'friday', label: 'Fri' },
    { key: 'saturday', label: 'Sat' },
    { key: 'sunday', label: 'Sun' },
  ];

  const getWorkingHoursTooltip = useCallback((resource: typeof resources[number]): string => {
    const profile = (resource.useCustomWorkingProfile && resource.customWorkingProfile)
      ? resource.customWorkingProfile
      : settings.globalWorkingProfile;

    const lines = DAYS_ORDER
      .filter(({ key }) => profile[key] > 0)
      .map(({ key, label }) => `${label}: ${profile[key]}h`);

    if (lines.length === 0) return 'No working hours configured';

    const header = (resource.useCustomWorkingProfile && resource.customWorkingProfile)
      ? 'Working Hours (Custom Profile)'
      : 'Working Hours';

    return `${header}\n${lines.join('\n')}`;
  }, [settings.globalWorkingProfile]);

  // Calculate utilization based on selected period
  const calculateUtilization = (resourceId: string): number => {
    const now = new Date();
    let periodStart: Date;
    let periodEnd: Date;

    switch (utilizationPeriod) {
      case 'day':
        periodStart = startOfDay(now);
        periodEnd = endOfDay(now);
        break;
      case 'week':
        periodStart = startOfWeek(now, { weekStartsOn: 1 }); // Monday
        periodEnd = endOfDay(addDays(periodStart, 6)); // Sunday
        break;
      case 'month':
        periodStart = startOfMonth(now);
        periodEnd = endOfMonth(now);
        break;
      case 'quarter':
        periodStart = startOfQuarter(now);
        periodEnd = endOfQuarter(now);
        break;
      case 'year':
        periodStart = startOfYear(now);
        periodEnd = endOfYear(now);
        break;
      default:
        periodStart = startOfMonth(now);
        periodEnd = endOfMonth(now);
    }

    // Find the resource to pass to the calculation function
    const resource = resources.find(r => String(r.id) === String(resourceId));
    
    return calculateResourceUtilization(
      resourceId, 
      bookings, 
      periodStart, 
      periodEnd, 
      resource, 
      settings.globalWorkingProfile
    );
  };

  // Get unique departments for filter
  const departments = useMemo(() => {
    return Array.from(
      new Set(
        resources
          .filter((r) => String(r.id) !== 'unallocated' && r.department)
          .map((r) => r.department)
      )
    ).sort() as string[];
  }, [resources]);

  // Get unique roles for filter
  const roles = useMemo(() => {
    return Array.from(
      new Set(
        resources
          .filter((r) => String(r.id) !== 'unallocated' && r.role)
          .map((r) => r.role as string)
      )
    ).sort();
  }, [resources]);

  // Get unique locations for filter (dynamic from current data)
  const locationOptions = useMemo(() => {
    const uniqueLocations = Array.from(
      new Set(
        resources
          .filter((r) => String(r.id) !== 'unallocated' && r.location)
          .map((r) => r.location as string)
      )
    ).sort();
    return uniqueLocations.map((loc) => ({ value: loc, label: loc }));
  }, [resources]);

  // Get unique skills for filter
  const skillOptions = useMemo(() => {
    const allSkills = resources
      .filter((r) => String(r.id) !== 'unallocated' && r.skills && r.skills.length > 0)
      .flatMap((r) => r.skills!.map((s) => (typeof s === 'string' ? s : s.name)));
    const unique = Array.from(new Set(allSkills)).sort();
    return unique.map((s) => ({ value: s, label: s }));
  }, [resources]);

  // Filter resources
  const filteredResources = useMemo(() => {
    return resources.filter((resource) => {
      // Exclude the special "Unallocated" system resource from the Resources page
      if (String(resource.id) === 'unallocated') {
        return false;
      }

      // Name search
      const matchesSearch =
        searchQuery === '' ||
        resource.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (resource.email && resource.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (resource.skills && resource.skills.some((skill) =>
          skill.name.toLowerCase().includes(searchQuery.toLowerCase())
        ));

      // Department filter (multi-select)
      const matchesDepartment =
        departmentFilter.length === 0 || (resource.department != null && departmentFilter.includes(resource.department));

      // Role filter (multi-select)
      const matchesRole =
        roleFilter.length === 0 || (resource.role != null && roleFilter.includes(resource.role));

      // Location filter (multi-select — must match any of the selected locations)
      const matchesLocation =
        locationFilter.length === 0 ||
        (resource.location != null && locationFilter.includes(resource.location));

      // Status filter (multi-select): active = available/busy/active, inactive = off/inactive
      const matchesStatus =
        statusFilter.length === 0 ||
        (statusFilter.includes('active') && (resource.status === 'available' || resource.status === 'busy' || resource.status === 'active')) ||
        (statusFilter.includes('inactive') && (resource.status === 'off' || resource.status === 'inactive'));

      // Skill filter — resource must have ALL selected skills
      const matchesSkills =
        skillFilter.length === 0 ||
        (resource.skills != null &&
          skillFilter.every((s) =>
            resource.skills!.some((rs) => (typeof rs === 'string' ? rs : rs.name) === s)
          ));

      return matchesSearch && matchesDepartment && matchesRole && matchesLocation && matchesStatus && matchesSkills;
    });
  }, [resources, searchQuery, departmentFilter, roleFilter, locationFilter, statusFilter, skillFilter]);

  // Sorted resources for the table view
  const sortedResources = useMemo(() => {
    const getValue = (r: typeof resources[number], field: SortField): string => {
      switch (field) {
        case 'name': return r.name ?? '';
        case 'role': return r.role ?? '';
        case 'department': return r.department ?? '';
        case 'email': return r.email ?? '';
        case 'phone': return r.phone ?? '';
        case 'location': return r.location ?? '';
        case 'type': return r.type ?? '';
      }
    };
    return [...filteredResources].sort((a, b) => {
      const aVal = getValue(a, sortField).toLowerCase();
      const bVal = getValue(b, sortField).toLowerCase();
      const cmp = aVal.localeCompare(bVal);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filteredResources, sortField, sortDir]);

  // Paginated slice
  const totalPages = Math.max(1, Math.ceil(sortedResources.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const pagedResources = sortedResources.slice((safePage - 1) * pageSize, safePage * pageSize);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDir === 'asc') {
        setSortDir('desc');
      } else {
        // third click → reset back to default (name asc)
        setSortField('name');
        setSortDir('asc');
      }
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  // Clear all filters at once
  const clearAllFilters = () => {
    setSearchQuery('');
    setDepartmentFilter([]);
    setRoleFilter([]);
    setLocationFilter([]);
    setStatusFilter([]);
    setSkillFilter([]);
    setPendingDepartmentFilter([]);
    setPendingRoleFilter([]);
    setPendingLocationFilter([]);
    setPendingStatusFilter([]);
    setPendingSkillFilter([]);
  };

  // Build active filter chips for the summary row
  const activeFilters = useMemo(() => {
    const chips: { key: string; label: string; clear: () => void }[] = [];
    if (searchQuery)
      chips.push({ key: 'search', label: `Search: "${searchQuery}"`, clear: () => setSearchQuery('') });
    departmentFilter.forEach((dept) =>
      chips.push({ key: `dept-${dept}`, label: `Dept: ${dept}`, clear: () => setDepartmentFilter((prev) => prev.filter((d) => d !== dept)) })
    );
    roleFilter.forEach((role) =>
      chips.push({ key: `role-${role}`, label: `Role: ${role}`, clear: () => setRoleFilter((prev) => prev.filter((r) => r !== role)) })
    );
    locationFilter.forEach((loc) =>
      chips.push({ key: `loc-${loc}`, label: `Location: ${loc}`, clear: () => setLocationFilter((prev) => prev.filter((l) => l !== loc)) })
    );
    statusFilter.forEach((status) =>
      chips.push({ key: `status-${status}`, label: `Status: ${status.charAt(0).toUpperCase() + status.slice(1)}`, clear: () => setStatusFilter((prev) => prev.filter((s) => s !== status)) })
    );
    skillFilter.forEach((skill) =>
      chips.push({ key: `skill-${skill}`, label: `Skill: ${skill}`, clear: () => setSkillFilter((prev) => prev.filter((s) => s !== skill)) })
    );
    return chips;
  }, [searchQuery, departmentFilter, roleFilter, locationFilter, statusFilter, skillFilter]);

  // Count active drawer filters for the badge indicator
  const drawerFilterCount = useMemo(() => {
    return departmentFilter.length + roleFilter.length + locationFilter.length + statusFilter.length + skillFilter.length;
  }, [departmentFilter, roleFilter, locationFilter, statusFilter, skillFilter]);

  // Sync pending state when the filter drawer opens
  const handleOpenFilterDrawer = useCallback(() => {
    setPendingDepartmentFilter([...departmentFilter]);
    setPendingRoleFilter([...roleFilter]);
    setPendingLocationFilter([...locationFilter]);
    setPendingStatusFilter([...statusFilter]);
    setPendingSkillFilter([...skillFilter]);
    setIsFilterDrawerOpen(true);
  }, [departmentFilter, roleFilter, locationFilter, statusFilter, skillFilter]);

  // Apply pending filters
  const applyPendingFilters = useCallback(() => {
    setDepartmentFilter([...pendingDepartmentFilter]);
    setRoleFilter([...pendingRoleFilter]);
    setLocationFilter([...pendingLocationFilter]);
    setStatusFilter([...pendingStatusFilter]);
    setSkillFilter([...pendingSkillFilter]);
    setIsFilterDrawerOpen(false);
  }, [pendingDepartmentFilter, pendingRoleFilter, pendingLocationFilter, pendingStatusFilter, pendingSkillFilter]);

  // Clear all pending filters and apply immediately
  const clearAndApplyFilters = useCallback(() => {
    setPendingDepartmentFilter([]);
    setPendingRoleFilter([]);
    setPendingLocationFilter([]);
    setPendingStatusFilter([]);
    setPendingSkillFilter([]);
    setDepartmentFilter([]);
    setRoleFilter([]);
    setLocationFilter([]);
    setStatusFilter([]);
    setSkillFilter([]);
    setIsFilterDrawerOpen(false);
  }, []);

  // Count pending drawer filters for the badge in the drawer header
  const pendingFilterCount = useMemo(() => {
    return pendingDepartmentFilter.length + pendingRoleFilter.length + pendingLocationFilter.length + pendingStatusFilter.length + pendingSkillFilter.length;
  }, [pendingDepartmentFilter, pendingRoleFilter, pendingLocationFilter, pendingStatusFilter, pendingSkillFilter]);

  // --- Bulk selection helpers ---
  const toggleResourceSelection = useCallback((id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.size === filteredResources.length) return new Set();
      return new Set(filteredResources.map((r) => String(r.id)));
    });
  }, [filteredResources]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const selectedResources = useMemo(
    () => filteredResources.filter((r) => selectedIds.has(String(r.id))).map((r) => ({ id: String(r.id), name: r.name })),
    [filteredResources, selectedIds]
  );

  // Handle view details click
  const handleViewDetails = (resource: typeof resources[number]) => {
    if (!canViewResourceDetails()) {
      toast.error('You do not have permission to view resource details.');
      return;
    }
    setSelectedResource(resource);
    setIsDetailsModalOpen(true);
  };

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden relative">
    <OnboardingReturnBanner />
    <div className="w-full px-6 py-4 space-y-3">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
            <Users className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold leading-tight">Resource Directory</h1>
            <p className="text-muted-foreground text-sm mt-0.5">
              Comprehensive catalogue of all resources across your organisation
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground text-right">
            <span className="font-semibold text-foreground">{filteredResources.length}</span>
            {' of '}
            <span className="font-semibold text-foreground">{resources.length - 1}</span>
            {' resources'}
          </span>
          <DataModeToggle screen="resources" />
          <HelpButton />
        </div>
      </div>

      {/* ── Filter Panel ──────────────────────────────────────────────── */}
      <div className="flex flex-col gap-2">

        {/* Row A — Search + Filter button + View toggle */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Search by name, email or skill..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-10"
            />
          </div>

          {/* Filter funnel button */}
          <Button
            variant="outline"
            size="icon"
            className="relative h-10 w-10 shrink-0"
            onClick={handleOpenFilterDrawer}
            aria-label="Open filters"
          >
            <Filter className="h-4 w-4" />
            {drawerFilterCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground leading-none">
                {drawerFilterCount}
              </span>
            )}
          </Button>

          {/* Column config (list view only) */}
          {viewMode === 'grid' && (
            <Button
              variant="outline"
              size="icon"
              className="relative h-10 w-10 shrink-0"
              aria-label="Configure columns"
              onClick={() => setIsResColConfigOpen(true)}
            >
              <SlidersHorizontal className="h-4 w-4" />
              {visibleResCols.size < ALL_RESOURCE_COLS.length && (
                <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground leading-none">
                  {ALL_RESOURCE_COLS.length - visibleResCols.size}
                </span>
              )}
            </Button>
          )}

          {/* View toggle */}
          <div className="shrink-0">
            <ToggleGroup
              type="single"
              value={viewMode}
              onValueChange={(value) => value && setViewMode(value as ViewMode)}
              variant="outline"
            >
              <ToggleGroupItem value="card" aria-label="Card view">
                <Grid3x3 className="h-4 w-4" />
              </ToggleGroupItem>
              <ToggleGroupItem value="grid" aria-label="List view">
                <List className="h-4 w-4" />
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>



      </div>

      {/* ── Configure Columns Sheet ──────────────────────────────────────── */}
      <Sheet open={isResColConfigOpen} onOpenChange={setIsResColConfigOpen}>
        <SheetContent side="right" className="w-[440px] sm:max-w-[440px] flex flex-col p-0 gap-0">
          {/* Header */}
          <SheetHeader className="px-6 pt-6 pb-5 border-b shrink-0">
            <div className="flex items-center gap-3 pr-8">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
                <Settings2 className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <SheetTitle className="text-base font-semibold leading-tight">
                  Customise Columns
                </SheetTitle>
                <SheetDescription className="text-xs mt-0.5 leading-relaxed">
                  Toggle columns on or off to personalise the resource table.
                </SheetDescription>
              </div>
            </div>

            {/* Visible count + reset */}
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold tabular-nums shrink-0">
                  {visibleResCols.size}
                </div>
                <span className="text-sm text-muted-foreground">
                  of {ALL_RESOURCE_COLS.length} columns showing
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                onClick={resetResCols}
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Reset defaults
              </Button>
            </div>
          </SheetHeader>

          {/* Column list */}
          <ScrollArea className="flex-1 min-h-0">
            <ScrollAreaViewport>
              <div className="px-4 py-4 space-y-2">
                {ALL_RESOURCE_COLS.map((col) => {
                  const isVisible = visibleResCols.has(col.key);
                  return (
                    <div
                      key={col.key}
                      className={[
                        'flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-colors duration-150',
                        isVisible && !col.locked
                          ? 'bg-card border-border hover:border-border/80 hover:bg-accent/30'
                          : col.locked
                            ? 'bg-muted/20 border-border/40'
                            : 'bg-muted/10 border-border/30',
                      ].join(' ')}
                    >
                      {/* Drag handle placeholder (visual parity) */}
                      <GripVertical className={['h-4 w-4 shrink-0', col.locked ? 'text-muted-foreground/20' : 'text-muted-foreground/30'].join(' ')} />

                      {/* Text */}
                      <div className="flex-1 min-w-0">
                        <p className={['text-sm font-medium leading-snug', !isVisible ? 'text-muted-foreground' : 'text-foreground'].join(' ')}>
                          {col.label}
                        </p>
                        <p className="text-xs text-muted-foreground/60 mt-0.5 truncate leading-tight">
                          {col.description}
                        </p>
                      </div>

                      {/* Toggle */}
                      <div className="shrink-0 flex items-center gap-3 ml-auto">
                        {col.locked ? (
                          <span className="text-[10px] font-medium text-muted-foreground/50 italic">Required</span>
                        ) : (
                          <Switch
                            checked={isVisible}
                            onCheckedChange={() => toggleResCol(col.key)}
                            aria-label={`${isVisible ? 'Hide' : 'Show'} ${col.label} column`}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* ── Filter Drawer ──────────────────────────────────────────────── */}
      <Sheet open={isFilterDrawerOpen} onOpenChange={setIsFilterDrawerOpen}>
        <SheetContent side="right" className="w-[380px] sm:max-w-[380px] flex flex-col p-0 gap-0">

          {/* Header */}
          <SheetHeader className="px-4 pt-4 pb-3 border-b shrink-0 gap-0">
            <div className="flex items-center gap-2">
              <SheetTitle className="text-sm font-semibold">Filters</SheetTitle>
              <Badge
                variant="secondary"
                className="flex items-center gap-1 px-2.5 py-0.5 h-6 text-[11px] font-medium rounded-full bg-primary/10 text-primary border-0 hover:bg-primary/10"
              >
                <Filter className="h-3 w-3" />
                {pendingFilterCount} {pendingFilterCount === 1 ? 'filter' : 'filters'} selected
              </Badge>
            </div>
          </SheetHeader>

          {/* Scrollable filter list */}
          <ScrollArea className="flex-1">
            <ScrollAreaViewport>
              <div className="px-4 py-4 space-y-4">

                {/* Skills */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Skills</label>
                  <MultiSelect
                    options={skillOptions}
                    placeholder="Select skills..."
                    value={pendingSkillFilter}
                    onValueChange={setPendingSkillFilter}
                    emptyMessage="No skills found."
                  />
                </div>

                <Separator />

                {/* Department */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Department</label>
                  <MultiSelect
                    options={departments.map((dept) => ({ value: dept, label: dept }))}
                    placeholder="All Departments"
                    value={pendingDepartmentFilter}
                    onValueChange={setPendingDepartmentFilter}
                    emptyMessage="No departments found."
                  />
                </div>

                <Separator />

                {/* Role */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Role</label>
                  <MultiSelect
                    options={roles.map((role) => ({ value: role, label: role }))}
                    placeholder="All Roles"
                    value={pendingRoleFilter}
                    onValueChange={setPendingRoleFilter}
                    emptyMessage="No roles found."
                  />
                </div>

                <Separator />

                {/* Location */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Location</label>
                  <MultiSelect
                    options={locationOptions}
                    placeholder="All Locations"
                    value={pendingLocationFilter}
                    onValueChange={setPendingLocationFilter}
                    emptyMessage="No locations found."
                  />
                </div>

                <Separator />

                {/* Status */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Status</label>
                  <MultiSelect
                    options={[
                      { value: 'active', label: 'Active' },
                      { value: 'inactive', label: 'Inactive' },
                    ]}
                    placeholder="All Statuses"
                    value={pendingStatusFilter}
                    onValueChange={setPendingStatusFilter}
                    emptyMessage="No statuses found."
                  />
                </div>

              </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>

          {/* Footer */}
          <SheetFooter className="px-4 py-3 border-t shrink-0 flex-row items-center justify-between gap-2">
            <Button
              variant="ghost"
              className="h-9 gap-1.5 text-muted-foreground hover:text-foreground px-2"
              onClick={clearAndApplyFilters}
              disabled={pendingFilterCount === 0}
            >
              <X className="h-3.5 w-3.5" />
              Clear Filters
            </Button>
            <Button
              className="h-9 gap-1.5 px-5"
              onClick={applyPendingFilters}
            >
              <Check className="h-3.5 w-3.5" />
              Apply Filters
            </Button>
          </SheetFooter>

        </SheetContent>
      </Sheet>

      {/* Select-all row */}
      {filteredResources.length > 0 && (
        <div className="flex items-center gap-2 py-1">
          <Checkbox
            checked={selectedIds.size === filteredResources.length && filteredResources.length > 0}
            onCheckedChange={toggleSelectAll}
            aria-label="Select all resources"
          />
          <span className="text-sm text-muted-foreground">
            {selectedIds.size > 0
              ? `${selectedIds.size} selected`
              : 'Select all'}
          </span>
        </div>
      )}

      {/* Resources Card View */}
      {viewMode === 'card' && (
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {filteredResources.length === 0 ? (
            <div className="col-span-full text-center py-12 text-muted-foreground">
              No resources found
            </div>
          ) : (
            filteredResources.map((resource) => {
              const isActive = resource.status === 'available' || resource.status === 'busy' || resource.status === 'active';
              return (
                <Card
                  key={resource.id}
                  onClick={() => handleViewDetails(resource)}
                  className={`overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-primary/40 hover:-translate-y-0.5 group ${selectedIds.has(String(resource.id)) ? 'ring-2 ring-primary' : ''}`}
                >
                  {/* Header Image / Initials Fallback */}
                  <div className="relative h-32">
                    {isValidPhotoUrl(resource.photoUrl) ? (
                      <>
                        <img
                          src={resource.photoUrl}
                          alt={resource.name}
                          className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
                          onError={(e) => {
                            // Hide broken image, show initials fallback
                            const target = e.currentTarget;
                            target.style.display = 'none';
                            const fallback = target.nextElementSibling as HTMLElement;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                        <div className="w-full h-full flex items-center justify-center bg-[oklch(50%_0.0896_194.8)]" style={{ display: 'none' }}>
                          <span className="text-3xl font-semibold text-white/90 tracking-wider">
                            {getInitials(resource.name)}
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-[oklch(50%_0.0896_194.8)]">
                        <span className="text-3xl font-semibold text-white/90 tracking-wider">
                          {getInitials(resource.name)}
                        </span>
                      </div>
                    )}
                    {/* Checkbox overlay — top left */}
                    <div
                      className={`absolute top-2 left-2 z-10 ${selectedIds.has(String(resource.id)) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}
                      onClick={(e) => toggleResourceSelection(String(resource.id), e)}
                    >
                      <Checkbox
                        checked={selectedIds.has(String(resource.id))}
                        className="h-5 w-5 border-2 border-white bg-background/80 backdrop-blur-sm"
                        aria-label={`Select ${resource.name}`}
                      />
                    </div>
                    {/* Active / Inactive badge — top right */}
                    <div className="absolute top-2 right-2">
                      <span className={[
                        'inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold backdrop-blur-sm',
                        isActive
                          ? 'bg-green-100 text-green-700'
                          : 'bg-zinc-600 text-zinc-300',
                      ].join(' ')}>
                        {isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>

                  <CardContent className="pt-4 pb-4 px-4">
                    <div className="flex flex-col space-y-3">
                      {/* Name only */}
                      <div>
                        <h3 className="font-semibold text-base leading-tight group-hover:text-primary transition-colors">{resource.name}</h3>
                      </div>

                      {/* Details */}
                      <div className="space-y-2 text-xs">
                        <div className="flex items-center gap-2">
                          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-muted flex-shrink-0">
                            <Briefcase className="h-3.5 w-3.5 text-muted-foreground" />
                          </div>
                          <span className="text-foreground font-medium">{resource.role}</span>
                        </div>

                        <div className="flex items-center gap-2">
                          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-muted flex-shrink-0">
                            <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                          </div>
                          <span className="text-muted-foreground">{resource.department || '-'}</span>
                        </div>

                        <div className="flex items-center gap-2">
                          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-muted flex-shrink-0">
                            <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                          </div>
                          <span className="text-muted-foreground">{resource.location || '-'}</span>
                        </div>

                        <div className="flex items-center gap-2">
                          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-muted flex-shrink-0">
                            <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                          </div>
                          <span className="text-muted-foreground truncate">{resource.email || '-'}</span>
                        </div>
                      </div>

                      {/* Top Skills */}
                      {resource.skills && resource.skills.length > 0 && (
                        <div className="pt-3 border-t">
                          <div className="text-xs font-medium text-muted-foreground mb-2">Top Skills</div>
                          <div className="flex flex-wrap gap-1.5">
                            {resource.skills.slice(0, 2).map((skill, index) => (
                              <div 
                                key={index}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-cyan-50 text-cyan-700 text-xs font-medium"
                              >
                                <span>{typeof skill === 'string' ? skill : skill.name}</span>
                                <span className="text-cyan-600 text-[10px]">{'★'.repeat(typeof skill === 'string' ? 5 : skill.rating)}</span>
                              </div>
                            ))}
                            {resource.skills.length > 2 && (
                              <div className="inline-flex items-center px-2 py-1 rounded-md bg-muted text-muted-foreground text-xs font-medium">
                                +{resource.skills.length - 2}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* View Details Button */}
                      <div className="pt-3 border-t">
                        <button className="w-full text-xs font-semibold text-primary border border-primary/30 hover:border-primary/60 hover:bg-primary/5 transition-all duration-150 rounded-md py-2 flex items-center justify-center gap-1.5">
                          <Eye className="h-3.5 w-3.5" />
                          View Details
                        </button>
                      </div>

                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}

      {/* Resources List View */}
      {viewMode === 'grid' && (() => {

        const SortIcon = ({ field }: { field: SortField }) => {
          if (sortField !== field) return <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground/60 ml-1 shrink-0" />;
          return sortDir === 'asc'
            ? <ChevronUp className="h-3.5 w-3.5 text-foreground ml-1 shrink-0" />
            : <ChevronDown className="h-3.5 w-3.5 text-foreground ml-1 shrink-0" />;
        };

        const getTypeLabel = (type: string) => {
          switch (type) {
            case 'permanent':   return 'Person';
            case 'contractor':  return 'Contractor';
            case 'agency':      return 'Agency';
            default:            return type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Person';
          }
        };

        return (
          <div className="flex flex-col gap-0 rounded-lg border overflow-hidden" data-tour="resources-table">
            {/* Table */}
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40 hover:bg-muted/40">
                  <TableHead className="w-[40px] px-3">
                    <Checkbox
                      checked={selectedIds.size === filteredResources.length && filteredResources.length > 0}
                      onCheckedChange={toggleSelectAll}
                      aria-label="Select all resources"
                    />
                  </TableHead>
                  <TableHead className="w-[52px]" />
                  {ALL_RESOURCE_COLS.filter((col) => visibleResCols.has(col.key)).map((col) => (
                    <TableHead
                      key={col.key}
                      className={`cursor-pointer select-none`}
                      onClick={() => handleSort(col.key as SortField)}
                    >
                      <div className="flex items-center text-xs font-semibold text-foreground/80">
                        {col.label}
                        <SortIcon field={col.key as SortField} />
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedResources.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleResCols.size + 2} className="text-center py-12 text-muted-foreground">
                      No resources found
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedResources.map((resource) => {
                    const isActive = resource.status === 'available' || resource.status === 'busy' || resource.status === 'active';
                    return (
                      <TableRow
                        key={resource.id}
                        className={`h-[60px] cursor-pointer transition-colors even:bg-muted/30 ${selectedIds.has(String(resource.id)) ? 'bg-primary/5 hover:bg-primary/8' : 'hover:bg-muted/40'}`}
                        onClick={() => handleViewDetails(resource)}
                      >
                        {/* Checkbox */}
                        <TableCell className="px-3 align-middle" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedIds.has(String(resource.id))}
                            onCheckedChange={() => {
                              setSelectedIds((prev) => {
                                const next = new Set(prev);
                                const id = String(resource.id);
                                if (next.has(id)) next.delete(id);
                                else next.add(id);
                                return next;
                              });
                            }}
                            aria-label={`Select ${resource.name}`}
                          />
                        </TableCell>

                        {/* Avatar */}
                        <TableCell className="align-middle">
                          <div className="relative inline-block">
                            <Avatar className="h-9 w-9 rounded-full">
                              {isValidPhotoUrl(resource.photoUrl) && (
                                <AvatarImage src={resource.photoUrl} alt={resource.name} />
                              )}
                              <AvatarFallback className="rounded-full bg-primary/80 text-primary-foreground font-semibold text-xs">
                                {getInitials(resource.name)}
                              </AvatarFallback>
                            </Avatar>
                            <span className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-background ${isActive ? 'bg-green-500' : 'bg-zinc-400'}`} />
                          </div>
                        </TableCell>

                        {/* Name */}
                        {visibleResCols.has('name') && (
                          <TableCell className="align-middle font-semibold text-sm text-foreground">
                            {resource.name}
                          </TableCell>
                        )}

                        {/* Role */}
                        {visibleResCols.has('role') && (
                          <TableCell className="align-middle text-sm text-primary font-medium">
                            {resource.role || '-'}
                          </TableCell>
                        )}

                        {/* Department — pill */}
                        {visibleResCols.has('department') && (
                          <TableCell className="align-middle">
                            {resource.department ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full border border-border text-xs font-medium text-foreground bg-background">
                                {resource.department}
                              </span>
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </TableCell>
                        )}

                        {/* Email — with icon */}
                        {visibleResCols.has('email') && (
                          <TableCell className="align-middle text-sm text-muted-foreground">
                            {resource.email ? (
                              <span className="inline-flex items-center gap-1.5">
                                <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
                                <span className="truncate max-w-[200px]">{resource.email}</span>
                              </span>
                            ) : (
                              <span>-</span>
                            )}
                          </TableCell>
                        )}

                        {/* Phone */}
                        {visibleResCols.has('phone') && (
                          <TableCell className="align-middle text-sm text-muted-foreground">
                            {resource.phone || '-'}
                          </TableCell>
                        )}

                        {/* Location */}
                        {visibleResCols.has('location') && (
                          <TableCell className="align-middle text-sm text-muted-foreground">
                            {resource.location || '-'}
                          </TableCell>
                        )}

                        {/* Type — badge */}
                        {visibleResCols.has('type') && (
                          <TableCell className="align-middle">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary text-primary-foreground">
                              {getTypeLabel(resource.type)}
                            </span>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            {/* Pagination footer */}
            <div className="flex items-center justify-between px-4 py-3 border-t bg-background text-sm text-muted-foreground">
              {/* Rows per page */}
              <div className="flex items-center gap-2">
                <span className="text-xs">Rows per page</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}
                >
                  <SelectTrigger className="h-8 w-[70px] text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[10, 25, 50, 100].map((n) => (
                      <SelectItem key={n} value={String(n)} className="text-xs">{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Page info */}
              <span className="text-xs">
                Page {safePage} of {totalPages}
              </span>

              {/* Page navigator */}
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  disabled={safePage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="min-w-[32px] text-center text-xs font-medium border rounded-md h-8 flex items-center justify-center px-2 bg-muted/40">
                  {safePage}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  disabled={safePage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  aria-label="Next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Resource Details Modal */}
      <ResourceDetailsModal
        resource={selectedResource}
        open={isDetailsModalOpen}
        onOpenChange={setIsDetailsModalOpen}
      />

      {/* Bulk Selection Bar */}
      <BulkSelectionBar
        selectedCount={selectedIds.size}
        onClear={clearSelection}
        onBulkUpdate={() => setShowBulkUpdateDialog(true)}
      />

      {/* Bulk Update Company Access Dialog */}
      <BulkUpdateCompanyAccessDialog
        open={showBulkUpdateDialog}
        onOpenChange={setShowBulkUpdateDialog}
        selectedResources={selectedResources}
      />

      {selectedIds.size > 0 && <div className="h-16" />}
    </div>
      {!isDemo && isContextLoading && (
        <div className="absolute inset-0 bg-background/50 backdrop-blur-[1px] z-50 flex items-center justify-center">
          <div className="flex items-center gap-3 bg-background border rounded-lg px-5 py-3 shadow-lg">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <span className="text-sm font-medium">Loading Resources...</span>
          </div>
        </div>
      )}
    </div>
  );
}
